/**
 * «Зміна податкового призначення НА» (0453, рішення власника 2026-09-01) —
 * аналог 1С УТП «ИзменениеНалоговогоНазначенияОС». Список + редактор за
 * канонами (Норма #29): .card, DataTable, DocListFilters, DocumentToolbar,
 * .tch, AssetPickerModal (мультивибір активів), чернетки форми.
 *
 * Семантика: проведення міняє «Податкове призначення (ПДВ)» на картці
 * активу + ПДВ-коригування за ст.198.5 (компенсуючі НО Дт 6431 — Кт 6442 /
 * відновлення кредиту зворотною) від залишкової вартості.
 *
 * ч.915 (новий дизайн, Закон #10): редактор — DocLayout / DocBlock / DocRail
 * (пронумеровані блоки ліворуч, рейка праворуч). Список — без змін.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  assetTaxPurpose, ADJUSTMENT_METHOD_UA,
  type AssetTaxPurposeChange, type AssetTaxPurposeItem, type TaxAdjustmentMethod,
} from '@/api/assetTaxPurpose';
import { fixedAssets, intangibleAssets, lowValueAssets, type AssetKind, type AssetStatus, type FixedAsset, type LowValueAsset } from '@/api/assets';
import { ASSET_KIND_UA } from '@/api/assetsRegistry';
import { refs, type TaxPurposeAssetsCosts } from '@/api/refs';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePopover from '@/components/DatePopover';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import DocListFilters, { DocListFiltersRow, useDocListFilters } from '@/shared/components/DocListFilters';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import ImportedBadge from '@/shared/components/ImportedBadge';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import { useOrgNames } from '@/shared/hooks/useOrgNames';
import AssetPickerModal from './AssetPickerModal';
import TchTable from '@/shared/components/TchTable';

const fmt2 = (v: string | number | null | undefined) => v == null || v === '' ? ''
  : Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const errDetail = (e: unknown) => {
  const d = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof d === 'string' ? d : JSON.stringify(d ?? String(e));
};
const today = () => new Date().toISOString().slice(0, 10);

// ============================ Список =================================

export function AssetTaxPurposeListPage() {
  const navigate = useNavigate();
  const f = useDocListFilters();
  const orgNames = useOrgNames();
  const list = useQuery({
    queryKey: ['asset-tax-purpose', ...f.queryKey],
    queryFn: () => assetTaxPurpose.list({
      organization_id: f.orgId || undefined,
      posted: f.status === 'posted' ? true : f.status === 'draft' ? false : undefined,
      date_from: f.dateFrom, date_to: f.dateTo, limit: 500,
    }),
  });
  const cols = useMemo<ColumnDef<AssetTaxPurposeChange>[]>(() => [
    {
      key: 'document_number', header: '№', width: 150, importBadge: true,
      sortValue: (r) => r.document_number ?? '',
      cell: (r) => r.document_number,
    },
    {
      key: 'document_date', header: 'Дата', width: 160,
      sortValue: (r) => r.document_date ?? '',
      cell: (r) => r.document_date ? new Date(r.document_date).toLocaleDateString('uk-UA') : '',
    },
    {
      key: 'organization_id', header: 'Організація', width: 200,
      sortValue: (r) => orgNames.get(r.organization_id) ?? '',
      cell: (r) => orgNames.get(r.organization_id) ?? '',
    },
    {
      key: 'reason', header: 'Підстава',
      sortValue: (r) => r.reason ?? '',
      cell: (r) => r.reason ?? '',
    },
    {
      key: 'items', header: 'Активів', width: 100, align: 'right',
      sortValue: (r) => r.items?.length ?? 0,
      cell: (r) => r.items?.length ?? 0,
    },
    {
      key: 'vat', header: 'ПДВ разом', width: 130, align: 'right',
      sortValue: (r) => r.items.reduce((s, i) => s + Number(i.vat_amount ?? 0), 0),
      cell: (r) => fmt2(r.items.filter((i) => i.adjustment_method !== 'NONE')
        .reduce((s, i) => s + Number(i.vat_amount ?? 0), 0)),
    },
    {
      key: 'posted', header: 'Проведено', width: 110,
      sortValue: (r) => (r.posted ? 1 : 0),
      cell: (r) => (r.posted ? '✓ так' : 'ні'),
    },
  ], [orgNames]);
  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Податкове призначення НА</h2>
        <button className="primary" style={{ marginLeft: 'auto' }}
          onClick={() => navigate('/assets/tax-purpose/new')}>+ Новий документ</button>
        <span style={{ color: '#666' }}>Усього: {list.data?.total ?? 0}</span>
      </div>
      <DocListFiltersRow>
        <DocListFilters f={f} statusOptions={[
          { value: '', label: 'Усі статуси' },
          { value: 'posted', label: 'Проведені' },
          { value: 'draft', label: 'Чернетки' },
        ]} />
      </DocListFiltersRow>
      <DataTable<AssetTaxPurposeChange>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        onRowDoubleClick={(r) => navigate(`/assets/tax-purpose/${r.id}`)}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </div>
  );
}

// ============================ Редактор ===============================

// ч.915 (новий дизайн, Закон #10): блокування форми — fieldset у КОЖНОМУ блоці
// (канон ВМД): шапка блоку з діями лишається поза fieldset.
const FIELDSET_STYLE = { border: 'none', margin: 0, padding: 0, minWidth: 0 } as const;

type AnyAsset = (FixedAsset | LowValueAsset) & { residual_value?: string; tax_purpose_id?: string | null };
type Cand = { kind: AssetKind; a: AnyAsset };

function useAssetCandidates(orgId: string, wanted: AssetStatus[]) {
  const faQ = useQuery({ queryKey: ['fixed-assets-v2', orgId], queryFn: () => fixedAssets.list({ organization_id: orgId, limit: 500 }), enabled: !!orgId });
  const iaQ = useQuery({ queryKey: ['intangible-assets-v2', orgId], queryFn: () => intangibleAssets.list({ organization_id: orgId, limit: 500 }), enabled: !!orgId });
  const lvQ = useQuery({ queryKey: ['low-value-assets', orgId], queryFn: () => lowValueAssets.list({ organization_id: orgId, limit: 500 }), enabled: !!orgId });
  const all = useMemo<Cand[]>(() => [
    ...(faQ.data?.items ?? []).map((a) => ({ kind: 'FIXED_ASSET' as AssetKind, a: a as AnyAsset })),
    ...(iaQ.data?.items ?? []).map((a) => ({ kind: 'INTANGIBLE' as AssetKind, a: a as AnyAsset })),
    ...(lvQ.data?.items ?? []).map((a) => ({ kind: 'LOW_VALUE' as AssetKind, a: a as AnyAsset })),
  ], [faQ.data, iaQ.data, lvQ.data]);
  const byId = useMemo(() => new Map(all.map((c) => [c.a.id, c])), [all]);
  return { byId, candidates: all.filter(({ a }) => wanted.includes(a.status)) };
}

export function AssetTaxPurposeEditor() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate(); const qc = useQueryClient(); const confirm = useConfirm();
  const [sp] = useSearchParams();
  const { availableOrgs } = usePermissionsByOrg('documents.tax_purpose_adj_inventory.create');
  const docQ = useQuery({ queryKey: ['asset-tax-purpose', id], queryFn: () => assetTaxPurpose.get(id!), enabled: !isNew });
  const d = docQ.data;
  const purposesQ = useQuery({ queryKey: ['tax-purposes-assets-costs'], queryFn: refs.taxPurposesAssetsCosts });
  const purposes: TaxPurposeAssetsCosts[] = purposesQ.data ?? [];
  const purposeName = (pid?: string | null) => purposes.find((p) => p.id === pid)?.name_uk ?? '—';

  const [form, setForm] = useState({
    organization_id: sp.get('organization_id') ?? '', document_date: today(),
    reason: '', comment: '', rows: [] as AssetTaxPurposeItem[],
  });
  useEffect(() => { if (d) setForm({
    organization_id: d.organization_id, document_date: d.document_date.slice(0, 10),
    reason: d.reason ?? '', comment: d.comment ?? '', rows: d.items,
  }); }, [d]);
  useFormDraft(form, setForm, { ready: isNew || !!d });
  const orgEff = form.organization_id || d?.organization_id || availableOrgs[0]?.id || '';
  const isPosted = !!d?.posted; const imported = d?.external_source === '1c'; const locked = isPosted || imported;
  const { byId, candidates } = useAssetCandidates(orgEff, ['OPERATING', 'PARKED', 'CONSERVATION', 'REPAIR_LONG']);
  const used = new Set(form.rows.map((r) => r.fixed_asset_id ?? r.intangible_asset_id ?? r.low_value_asset_id ?? ''));
  const [error, setError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const defaultPurpose = purposes.find((p) => p.code === 'NOT_OBJECT')?.id ?? purposes[0]?.id ?? '';
  const addRows = (list: Cand[]) => setForm((f) => ({ ...f, rows: [...f.rows, ...list.map((c, j) => ({
    line_number: f.rows.length + j + 1, asset_kind: c.kind,
    fixed_asset_id: c.kind === 'FIXED_ASSET' ? c.a.id : null,
    intangible_asset_id: c.kind === 'INTANGIBLE' ? c.a.id : null,
    low_value_asset_id: c.kind === 'LOW_VALUE' ? c.a.id : null,
    new_tax_purpose_id: defaultPurpose,
    residual_value_snapshot: c.a.residual_value ?? '0',
    vat_rate_percent: '20', vat_amount_override: null,
    adjustment_method: 'NONE' as TaxAdjustmentMethod, item_note: null,
  }))] }));
  const upd = (i: number, patch: Partial<AssetTaxPurposeItem>) =>
    setForm({ ...form, rows: form.rows.map((x, j) => j === i ? { ...x, ...patch } : x) });
  const vatOf = (r: AssetTaxPurposeItem): number => r.vat_amount_override != null && r.vat_amount_override !== ''
    ? Number(r.vat_amount_override)
    : Math.round(Number(r.residual_value_snapshot || 0) * Number(r.vat_rate_percent || 0)) / 100;

  const payload = () => ({
    organization_id: orgEff, document_number: null, document_date: `${form.document_date}T12:00:00`,
    reason: form.reason || null, comment: form.comment || null,
    items: form.rows.map((r, i) => ({
      line_number: i + 1, asset_kind: r.asset_kind,
      fixed_asset_id: r.fixed_asset_id ?? null,
      intangible_asset_id: r.intangible_asset_id ?? null,
      low_value_asset_id: r.low_value_asset_id ?? null,
      new_tax_purpose_id: r.new_tax_purpose_id,
      residual_value_snapshot: r.residual_value_snapshot || '0',
      vat_rate_percent: r.vat_rate_percent || '0',
      vat_amount_override: r.vat_amount_override || null,
      adjustment_method: r.adjustment_method, item_note: r.item_note ?? null,
    })),
  });
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['asset-tax-purpose'] });
    qc.invalidateQueries({ queryKey: ['fixed-assets-v2'] });
    qc.invalidateQueries({ queryKey: ['intangible-assets-v2'] });
    qc.invalidateQueries({ queryKey: ['low-value-assets'] });
  };
  const save = useMutation({
    mutationFn: async (andPost: boolean) => {
      const doc = isNew ? await assetTaxPurpose.create(payload() as never)
        : await assetTaxPurpose.update(id!, payload() as never);
      if (andPost) {
        try { await assetTaxPurpose.post(doc.id); } catch (e) {
          // ч.1343: новий документ уже створено — відкриваємо його, інакше повторне «провести» створило б дубль.
          if (isNew) navigate(`/assets/tax-purpose/${doc.id}`, { replace: true });
          throw e;
        }
      }
      return doc;
    },
    onSuccess: (doc) => { invalidate(); if (isNew) navigate(`/assets/tax-purpose/${doc.id}`, { replace: true }); },
    onError: (e) => setError(errDetail(e)),
  });
  const unpost = useMutation({ mutationFn: () => assetTaxPurpose.unpost(id!), onSuccess: invalidate, onError: (e) => setError(errDetail(e)) });
  const remove = useMutation({ mutationFn: () => assetTaxPurpose.remove(id!), onSuccess: () => { invalidate(); navigate('/assets/tax-purpose'); }, onError: (e) => setError(errDetail(e)) });
  const canSave = !!orgEff && form.rows.length > 0 && form.rows.every((r) => r.new_tax_purpose_id);
  const totalVat = form.rows.filter((r) => r.adjustment_method !== 'NONE')
    .reduce((s, r) => s + vatOf(r), 0);
  // ч.915 (новий дизайн, Закон #10): «ще дій» у документа не було.
  const railActions: { label: string; title?: string; disabled?: boolean; onClick: () => void }[] = [];
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 2,
      filled: (orgEff ? 1 : 0) + (form.document_date ? 1 : 0) },
    { id: 'items', title: 'Активи', required: 1, filled: form.rows.length > 0 ? 1 : 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          {isNew ? 'Нова зміна податкового призначення НА' : `Податкове призначення НА ${d?.document_number ?? ''}`}
          {imported && <ImportedBadge source="1c" />}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginLeft: 'auto' }}>
          <DocumentToolbar onBackToList={() => navigate('/assets/tax-purpose')}
            onSave={() => save.mutate(false)} saveDisabled={!canSave || locked} savePending={save.isPending}
            postable={!imported} isPosted={isPosted} isNew={isNew}
            onPost={() => save.mutate(true)} postDisabled={!canSave} postPending={save.isPending}
            postTitle="Проведення міняє призначення на картках активів + ПДВ-коригування 6431/6442 (ст.198.5)"
            onUnpost={() => unpost.mutate()} unpostPending={unpost.isPending}
            deletable={!isPosted}
            onDelete={() => { confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); }); }}
            deletePending={remove.isPending}
            entriesDoc={{ type: 'doc.asset_tax_purpose_changes', id: isNew ? '' : id! }}
            railMode
            extraActions={railActions} />
        </div>
      </div>
      {/* ч.915 (новий дизайн, Закон #10): форма — пронумерованими блоками + рейка. */}
      <DocLayout rail={
        <DocRail blocks={blocks} actions={railActions} isNew={isNew}
          doc={{ type: 'doc.asset_tax_purpose_changes', id: isNew ? '' : id! }} />
      }>
      <DocBlock index={1} title="Основні дані">
      <fieldset disabled={locked} style={FIELDSET_STYLE}>
        <div className="doc-grid">
          <div className="field" style={{ gridColumn: 'span 3' }}><label>Організація *</label>
            <select value={orgEff} disabled={!isNew}
              onChange={(e) => setForm({ ...form, organization_id: e.target.value, rows: [] })}>
              {availableOrgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select></div>
          <div className="field" style={{ gridColumn: 'span 3' }}><label>Дата *</label>
            <DatePopover value={form.document_date} onChange={(v) => setForm({ ...form, document_date: v })} /></div>
          <div className="field" style={{ gridColumn: 'span 6' }}><label>Підстава (подія)</label>
            <input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
              placeholder="напр., передача у негосподарську діяльність" /></div>
        </div>
      </fieldset>
      </DocBlock>

      {/* ч.915: «+ додати активи» — у шапці блоку ТЧ (поза fieldset), лише коли форма редагована. */}
      <DocBlock index={2} title="Активи"
        actions={!locked ? (
          <button type="button" className="primary" onClick={() => setPickerOpen(true)}>+ додати активи</button>
        ) : undefined}>
      <fieldset disabled={locked} style={FIELDSET_STYLE}>
        <TchTable>
          <thead><tr><th>№</th><th>Актив</th><th>Призначення (було)</th><th>Нове призначення</th>
            <th style={{ textAlign: 'right' }}>Залишкова</th><th>% ПДВ</th>
            <th style={{ textAlign: 'right' }}>Сума ПДВ</th><th>Коригування</th><th>Примітка</th><th /></tr></thead>
          <tbody>
            {form.rows.length === 0 && <tr><td colSpan={10} style={{ color: 'var(--muted)' }}>Додайте активи.</td></tr>}
            {form.rows.map((r, i) => {
              const c = byId.get(r.fixed_asset_id ?? r.intangible_asset_id ?? r.low_value_asset_id ?? '');
              const oldName = isPosted ? purposeName(r.old_tax_purpose_id)
                : purposeName(c?.a.tax_purpose_id);
              return (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td>{c ? `${ASSET_KIND_UA[c.kind]} ${c.a.inventory_number} ${c.a.name}` : '…'}</td>
                  <td style={{ color: 'var(--muted)' }}>{oldName}</td>
                  <td>
                    <select value={r.new_tax_purpose_id} onChange={(e) => upd(i, { new_tax_purpose_id: e.target.value })}>
                      {purposes.map((p) => <option key={p.id} value={p.id}>{p.name_uk}</option>)}
                    </select>
                  </td>
                  <td><input style={{ textAlign: 'right', width: 110 }} value={r.residual_value_snapshot}
                    onChange={(e) => upd(i, { residual_value_snapshot: e.target.value })} /></td>
                  <td>
                    <select value={r.vat_rate_percent} onChange={(e) => upd(i, { vat_rate_percent: e.target.value })}>
                      {['20', '14', '7', '0'].map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <input style={{ textAlign: 'right', width: 100 }}
                      value={r.vat_amount_override ?? ''}
                      placeholder={fmt2(vatOf(r))}
                      title="Порожнє = авто (залишкова × %); введіть суму для ручного коригування"
                      onChange={(e) => upd(i, { vat_amount_override: e.target.value || null })} />
                  </td>
                  <td>
                    <select value={r.adjustment_method}
                      onChange={(e) => upd(i, { adjustment_method: e.target.value as TaxAdjustmentMethod })}>
                      {(Object.keys(ADJUSTMENT_METHOD_UA) as TaxAdjustmentMethod[])
                        .map((m) => <option key={m} value={m}>{ADJUSTMENT_METHOD_UA[m]}</option>)}
                    </select>
                  </td>
                  <td><input value={r.item_note ?? ''} onChange={(e) => upd(i, { item_note: e.target.value || null })} /></td>
                  <td><button type="button" className="danger"
                    onClick={() => setForm({ ...form, rows: form.rows.filter((_, j) => j !== i) })}>×</button></td>
                </tr>
              );
            })}
          </tbody>
          {form.rows.length > 0 && (
            <tfoot><tr>
              <td colSpan={6} style={{ textAlign: 'right' }}>Разом ПДВ-коригування:</td>
              <td style={{ textAlign: 'right' }}>{fmt2(totalVat)}</td>
              <td colSpan={3} />
            </tr></tfoot>
          )}
        </TchTable>
      </fieldset>
      {/* ч.915: модалка підбору — у блоці ТЧ, поза fieldset (як і було — поза блокуванням форми). */}
      {pickerOpen && (
        <AssetPickerModal orgId={orgEff} candidates={candidates} used={used}
          onAdd={addRows} onClose={() => setPickerOpen(false)} />
      )}
      </DocBlock>

      <DocBlock index={3} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <fieldset disabled={locked} style={FIELDSET_STYLE}>
          <div className="field" style={{ margin: 0 }}><label>Коментар</label>
            <input value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} /></div>
        </fieldset>
        <div>
          {/* ч.915: вкладення (рішення власника 2026-08-13: на всіх документах);
              панелі в редакторі не було — тип документа той самий, що в entriesDoc. */}
          <AttachmentsPanel organizationId={orgEff} documentType="doc.asset_tax_purpose_changes"
            documentId={isNew ? undefined : id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
      {error && <div style={{ color: '#dc2626', marginTop: '1rem', whiteSpace: 'pre-wrap' }}>{error}</div>}
    </div>
  );
}
